/**
 * Pure helpers for `ResponsibilityClaim` state transitions.
 *
 * Two flavors live here:
 *  1. Status-transition helpers (`nextStatus`, `isInFlight`) — Start It /
 *     Mark Done / Mark Not Done flow. Mirrors iOS
 *     `Views/Responsibilities/ResponsibilitiesView.swift` action handlers.
 *  2. Reschedule helpers (`acceptReschedule`, `declineReschedule`) — compute
 *     the field updates a doer applies when accepting or declining a
 *     reschedule request. Mirrors iOS `ResponsibilitiesView.swift:679-711`.
 *
 * The reschedule helpers return only field deltas; the caller passes them
 * to `updateDoc(ref, ...)`. This keeps the module DOM-free and Firestore-
 * free so it can be unit-tested without mocking either.
 */

import { Timestamp } from "firebase/firestore";
import type { ResponsibilityClaim } from "./models";

// ─── Status transitions (Wave L.1) ──────────────────────────────────────

export type ClaimStatus =
  | "claimed"
  | "in_progress"
  | "completed"
  | "skipped"
  | "reschedule_pending"
  | "reschedule_declined";

export type ClaimAction =
  | "start"
  | "markNotDone"
  | "complete"
  | "unclaim";

/**
 * Compute the next `status` value for a claim given the current status and
 * the user's action. Returns `null` when the action is not valid for the
 * current status (caller should ignore the click).
 *
 * Note: "unclaim" and "markNotDone" delete the claim entirely (matching iOS
 * `unclaimAction` / `markNotDoneAction`). They return null here because there
 * is no resulting status — the document is removed.
 */
export function nextStatus(
  current: ClaimStatus,
  action: ClaimAction,
): ClaimStatus | null {
  switch (action) {
    case "start":
      // Only a freshly claimed item can be started.
      return current === "claimed" ? "in_progress" : null;

    case "complete":
      // Both claimed and in_progress can be completed directly.
      return current === "claimed" || current === "in_progress"
        ? "completed"
        : null;

    case "markNotDone":
    case "unclaim":
      // Both delete the claim — no resulting status.
      return null;

    default:
      return null;
  }
}

/**
 * Returns true when the given status represents a claim the user has
 * actively taken on but not yet finished. Used to decide whether to render
 * the Start It / Mark Done / Mark Not Done cluster.
 */
export function isInFlight(status: ClaimStatus): boolean {
  return status === "claimed" || status === "in_progress";
}

// ─── Reschedule (Wave L.4) ──────────────────────────────────────────────

/**
 * Field updates for accepting a reschedule request.
 *
 * Behavior (mirrors iOS `acceptReschedule`):
 *  - `status` → "claimed"
 *  - If the request includes a `rescheduleProposedTime`, copy it into
 *    `effectiveTime` so the claim now reflects the new schedule.
 *  - Record the prior status as "reschedule_pending" so the audit trail is
 *    preserved.
 *
 * Note: iOS leaves the `reschedule*` request fields in place after acceptance
 * (it only nulls them on decline). We follow the same convention so the
 * "this was rescheduled" provenance is visible in the doc.
 */
export function acceptReschedule(claim: ResponsibilityClaim): Record<string, unknown> {
  const updates: Record<string, unknown> = {
    status: "claimed",
    priorStatus: "reschedule_pending",
  };

  if (claim.rescheduleProposedTime) {
    updates.effectiveTime = claim.rescheduleProposedTime;
  }

  return updates;
}

/**
 * Field updates for declining a reschedule request.
 *
 * Behavior (mirrors iOS `declineReschedule`):
 *  - `status` → revert to `claim.priorStatus` (or "claimed" if the prior
 *    status is missing, e.g. for older claims).
 *  - Clear all `reschedule*` request fields so the claim looks like it never
 *    had a pending request.
 *  - The original `dueDate` / `effectiveTime` are intentionally left untouched.
 */
export function declineReschedule(claim: ResponsibilityClaim): Record<string, unknown> {
  return {
    status: claim.priorStatus ?? "claimed",
    priorStatus: null,
    rescheduleRequestedAt: null,
    rescheduleRequestedBy: null,
    rescheduleProposedTime: null,
    rescheduleProposedDate: null,
    rescheduleNote: null,
  };
}

/**
 * Re-export of `Timestamp` so consumers and tests don't have to import it
 * separately when constructing fixture claims.
 */
export { Timestamp };
